import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormHelperText,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import { CustomInput } from "global/components";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import strings from "global/constants/StringConstants";
import { openSuccessNotification, handleErrorMessage } from "helpers/methods";
import {
  focusFirstInvalidField,
  InvalidFieldTarget,
} from "utils/accessibility";
import { useState } from "react";
import { updateRole, addRole } from "../SettingsService";
import {
  createRoleFormData,
  getResourceObj,
  mapFormDataToValues,
  roleFormValidation,
} from "./RoleManagementHelpers";
import {
  addResourceSx,
  createRoleButtonSx,
  fieldErrorSx,
  formBodySx,
  formDividerSx,
  formFooterSx,
  formLabelSx,
  formSectionDescriptionSx,
  formSectionHeaderSx,
  formSectionSx,
  formSectionTitleSx,
  formSelectSx,
  formTextFieldSx,
  removeResourceSx,
  removeResourceWrapperSx,
  resourceCardSx,
  resourceListWrapperSx,
  rolesCardSx,
  secondaryButtonSx,
  selectOptionSx,
  selectPlaceholderSx,
  selectValueSx,
} from "./Roles.styles";
import rolesTokens from "./Roles.tokens";
import {
  AddResourceIcon,
  RemoveResourceIcon,
} from "./components/RoleIcons";

interface AddUpdateRolesProps {
  name: string;
  setButtonClick: any;
  rowData: any;
  fetchRolesHandler: any;
  resources: any;
}

const ROLE_NAME_FIELD_ID = "role_management_role_name";
const RESOURCE_FIELD_ID = "role_management_select_resource_dropdown_";
const PERMISSIONS_FIELD_ID = "role_management_select_permission_dropdown_";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const { form } = rolesTokens;

export const AddUpdateRoles = (props: AddUpdateRolesProps) => {
  const [roleFormData, setRoleFormData] = useState(
    createRoleFormData(props.rowData),
  );
  const [loading, setLoading] = useState<boolean>(false);

  const isEdit = props.name === strings.editRole;
  /** Every resource is already spoken for, so there is nothing left to add. */
  const allResourcesSelected =
    props.resources.length === roleFormData?.resources.length;

  const handleAdd = () => {
    handleValidation(true) &&
      setRoleFormData({
        ...roleFormData,
        resources: [...roleFormData.resources, getResourceObj()],
      });
  };

  const deleteRoleHandler = (index: number) => {
    let tempRoleForm = { ...roleFormData };
    if (roleFormData.resources.length > 1) {
      tempRoleForm.resources.splice(index, 1);
    } else {
      tempRoleForm.resources = [getResourceObj()];
    }
    setRoleFormData(tempRoleForm);
  };

  const handleValidation = (isAddResourceValidation: boolean = false) => {
    const { isValid, errors } = roleFormValidation(
      roleFormData,
      isAddResourceValidation,
    );
    setRoleFormData(errors);
    if (!isValid) {
      const targets: InvalidFieldTarget[] = [
        errors.name.error && ROLE_NAME_FIELD_ID,
      ];
      errors.resources.forEach((resource: any, index: number) => {
        targets.push(resource.name.error && `${RESOURCE_FIELD_ID}${index}`);
        targets.push(
          resource.permissions.error && `${PERMISSIONS_FIELD_ID}${index}`,
        );
      });
      focusFirstInvalidField(targets);
    }
    return isValid;
  };

  const handleRoleNameChange = (e: any) => {
    if (!strings.characterRegex.test(e.target.value)) {
      setRoleFormData({
        ...roleFormData,
        name: {
          ...roleFormData.name,
          error: "Special characters or digits are not allowed.",
        },
      });
      return;
    }
    setRoleFormData({
      ...roleFormData,
      name: {
        value: e.target.value,
        error: "",
      },
    });
  };

  const handleResourceTypeChange = (e: any, resourceIndex: number) => {
    let tempRoleForm = { ...roleFormData };
    tempRoleForm.resources[resourceIndex].name.value = e.target.value;
    tempRoleForm.resources[resourceIndex].permissions.value = [];
    tempRoleForm.resources[resourceIndex].name.error = "";
    tempRoleForm.resources[resourceIndex].path.value =
      e.target.value.toLowerCase();
    setRoleFormData(tempRoleForm);
  };

  const handleResourcePermissionChange = (e: any, resourceIndex: number) => {
    let tempRoleForm = { ...roleFormData };
    tempRoleForm.resources[resourceIndex].permissions.value = e.target.value;
    tempRoleForm.resources[resourceIndex].permissions.error = "";
    setRoleFormData(tempRoleForm);
  };

  const addRoleHandler = async () => {
    try {
      if (handleValidation()) {
        setLoading(true);
        const body = mapFormDataToValues(roleFormData);
        if (isEdit) {
          await updateRole({ ...body, id: props.rowData?.id });
          openSuccessNotification(`${body.name} role has been updated`);
        } else {
          await addRole(body);
          openSuccessNotification("New Role has been added successfully");
        }
        props.setButtonClick(strings.rolesTable);
        await props.fetchRolesHandler();
        setLoading(false);
      }
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    }
  };

  const renderSectionHeader = (title: string, description: string) => (
    <Box sx={formSectionHeaderSx}>
      <Typography component="h2" sx={formSectionTitleSx}>
        {title}
      </Typography>
      <Typography component="p" sx={formSectionDescriptionSx}>
        {description}
      </Typography>
    </Box>
  );

  const renderRoleNameSection = () => (
    <Box sx={formSectionSx}>
      {renderSectionHeader(
        "Role details",
        "Give the role a name your team will recognise when assigning it.",
      )}
      <CustomInput
        id={ROLE_NAME_FIELD_ID}
        type="text"
        label="Role Name"
        placeHolder="Enter Role Name"
        name="roleName"
        required
        value={roleFormData?.name.value}
        onChange={handleRoleNameChange}
        error={roleFormData?.name.error}
        helperText={roleFormData?.name.error}
        sx={formTextFieldSx}
        labelSx={formLabelSx}
        labelSpacing={0}
      />
    </Box>
  );

  /** Resources already claimed by another row cannot be picked again. */
  const getAvailableResources = (item: any) =>
    props.resources.filter(
      (res: any) =>
        !roleFormData.resources.some(
          (resource: any) =>
            resource.name.value === res.name &&
            resource.name.value !== item.name.value,
        ),
    );

  const getPermissionsFor = (path: string) =>
    props.resources.find((resource: any) => resource.path === path)
      ?.permissions ?? [];

  const renderResourceRow = (item: any, resourceIndex: number) => {
    const isOnlyEmptyRow =
      roleFormData.resources.length === 1 &&
      !item.name.value &&
      item.permissions.value.length === 0;

    return (
      <Box
        key={`role-resource-${resourceIndex}`}
        role="group"
        aria-label={
          item.name.value
            ? `${item.name.value} resource permissions`
            : `Resource ${resourceIndex + 1}`
        }
        sx={resourceCardSx}
      >
        <Box>
          <InputLabel
            id={`role-resource-label-${resourceIndex}`}
            required
            sx={formLabelSx}
          >
            Resource
          </InputLabel>
          <Select
            SelectDisplayProps={{
              id: `${RESOURCE_FIELD_ID}${resourceIndex}`,
              "aria-invalid": Boolean(item.name.error) || undefined,
            }}
            labelId={`role-resource-label-${resourceIndex}`}
            name="name"
            sx={formSelectSx}
            error={Boolean(item.name.error)}
            aria-describedby={
              item.name.error
                ? `role-resource-error-${resourceIndex}`
                : undefined
            }
            value={item.name.value}
            onChange={(e: any) => handleResourceTypeChange(e, resourceIndex)}
            displayEmpty
            MenuProps={MenuProps}
            renderValue={() =>
              item?.name.value ? (
                <Typography component="span" sx={selectValueSx}>
                  {item.name.value}
                </Typography>
              ) : (
                <Typography component="span" sx={selectPlaceholderSx}>
                  Select Resources
                </Typography>
              )
            }
          >
            {getAvailableResources(item).map((resource: any) => (
              <MenuItem
                key={resource.name}
                value={resource.name}
                sx={selectOptionSx}
              >
                {resource.name}
              </MenuItem>
            ))}
          </Select>
          {item.name.error && (
            <FormHelperText
              id={`role-resource-error-${resourceIndex}`}
              sx={fieldErrorSx}
            >
              {item.name.error}
            </FormHelperText>
          )}
        </Box>

        <Box>
          <InputLabel
            id={`role-permissions-label-${resourceIndex}`}
            required
            sx={formLabelSx}
          >
            Permissions
          </InputLabel>
          <Select
            SelectDisplayProps={{
              id: `${PERMISSIONS_FIELD_ID}${resourceIndex}`,
              "aria-invalid": Boolean(item.permissions.error) || undefined,
            }}
            labelId={`role-permissions-label-${resourceIndex}`}
            name="permissions"
            sx={formSelectSx}
            value={item.permissions.value}
            error={Boolean(item.permissions.error)}
            aria-describedby={
              item.permissions.error
                ? `role-permissions-error-${resourceIndex}`
                : undefined
            }
            multiple
            disabled={!item.path.value}
            onChange={(e: any) =>
              handleResourcePermissionChange(e, resourceIndex)
            }
            displayEmpty
            MenuProps={MenuProps}
            renderValue={() =>
              item?.permissions?.value.length ? (
                <Typography component="span" sx={selectValueSx}>
                  {item.permissions.value.join(", ")}
                </Typography>
              ) : (
                <Typography component="span" sx={selectPlaceholderSx}>
                  {item.path.value
                    ? "Select Permission"
                    : "Pick a resource first"}
                </Typography>
              )
            }
          >
            {getPermissionsFor(item.path.value).map(
              (permission: any, permIndex: number) => (
                <MenuItem
                  key={permission}
                  value={permission}
                  sx={selectOptionSx}
                >
                  <Checkbox
                    id={`role_management_checkbox_${resourceIndex}_${permIndex}`}
                    checked={item?.permissions?.value.includes(permission)}
                    tabIndex={-1}
                    inputProps={{ "aria-hidden": true }}
                  />
                  <ListItemText
                    primary={permission}
                    primaryTypographyProps={{ sx: selectOptionSx }}
                  />
                </MenuItem>
              ),
            )}
          </Select>
          {item.permissions.error && (
            <FormHelperText
              id={`role-permissions-error-${resourceIndex}`}
              sx={fieldErrorSx}
            >
              {item.permissions.error}
            </FormHelperText>
          )}
        </Box>

        <Tooltip title="Remove resource" placement="top" describeChild>
          {/* Span keeps the tooltip alive once the button disables itself. */}
          <Box component="span" sx={removeResourceWrapperSx}>
            <IconButton
              id={`role_management_delete_role_${resourceIndex}`}
              onClick={() => deleteRoleHandler(resourceIndex)}
              disabled={isOnlyEmptyRow}
              aria-label={
                item.name.value
                  ? `Remove ${item.name.value} resource`
                  : `Remove resource ${resourceIndex + 1}`
              }
              sx={removeResourceSx}
            >
              <RemoveResourceIcon
                sx={{ width: form.removeIconSize, height: form.removeIconSize }}
              />
            </IconButton>
          </Box>
        </Tooltip>
      </Box>
    );
  };

  const renderResourcesSection = () => (
    <Box sx={formSectionSx}>
      {renderSectionHeader(
        "Resources and permissions",
        "Choose what this role can reach, and what it is allowed to do there.",
      )}
      <Box sx={resourceListWrapperSx}>
        {roleFormData?.resources?.map(renderResourceRow)}
        {!allResourcesSelected && (
          <Button
            id="role_management_add"
            onClick={handleAdd}
            sx={addResourceSx}
          >
            <AddResourceIcon
              sx={{ width: form.addIconSize, height: form.addIconSize }}
            />
            Add another resource
          </Button>
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <Box sx={rolesCardSx}>
        <Box sx={formBodySx}>
          {renderRoleNameSection()}
          <Divider sx={formDividerSx} />
          {renderResourcesSection()}
          <Box sx={formFooterSx}>
            <Button
              id="role_management_cancel_button"
              onClick={() => props.setButtonClick(strings.rolesTable)}
              sx={secondaryButtonSx}
            >
              Cancel
            </Button>
            <Button
              id="role_management_submit_button"
              variant="contained"
              onClick={addRoleHandler}
              sx={createRoleButtonSx}
            >
              {isEdit ? "Update" : "Submit"}
            </Button>
          </Box>
        </Box>
      </Box>
      <CustomLoader isLoading={loading} />
    </>
  );
};
